<div class="w-full max-w-5xl mx-auto">

  <!-- Header -->
  <div class="flex justify-between items-center mb-6">
    <div>
      <h2 class="text-xl font-bold text-gray-900 dark:text-gray-100">Groups</h2>
      <p class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Manage your organization's groups and members</p>
    </div>

    <!-- Create Group Button -->
    <button
      *ngIf="!isCreatingGroup()"
      (click)="onCreateGroupClick()"
      class="inline-flex items-center gap-2 px-4 py-2.5 bg-btn-primary text-btn-primary-text font-semibold rounded-xl shadow-sm hover:opacity-90 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-btn-primary/50 focus:ring-offset-2"
    >
      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clip-rule="evenodd" />
      </svg>
      Create Group
    </button>
  </div>

  <!-- Create Group Form -->
  <div *ngIf="isCreatingGroup()" class="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-2xl p-5 shadow-sm">
    <h3 class="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-4 flex items-center gap-2">
      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clip-rule="evenodd" />
      </svg>
      New Group
    </h3>
    <div class="flex flex-col sm:flex-row gap-3">
      <input
        type="text"
        [(ngModel)]="newGroupName"
        placeholder="Group name"
        class="flex-1 px-4 py-2.5 bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-200 text-sm shadow-sm"
        autofocus
      >
      <input
        type="text"
        [(ngModel)]="newGroupDescription"
        placeholder="Description (optional)"
        class="flex-1 px-4 py-2.5 bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-200 text-sm shadow-sm"
      >
      <div class="flex gap-2 self-end sm:self-auto">
        <button
          (click)="onSaveGroup()"
          class="px-5 py-2.5 bg-btn-primary text-btn-primary-text font-semibold rounded-xl shadow-sm hover:opacity-90 transition-all duration-200 text-sm"
        >
          Save
        </button>
        <button
          (click)="onCancelCreateGroup()"
          class="px-5 py-2.5 bg-white hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 font-semibold rounded-xl border border-gray-300 dark:border-gray-600 shadow-sm transition-all duration-200 text-sm"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>

  <!-- Groups List -->
  <div *ngIf="groups.length > 0; else noGroups" class="space-y-3">
    <div
      *ngFor="let group of groups; trackBy: trackById"
      class="group flex justify-between items-center p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl hover:border-indigo-300 dark:hover:border-indigo-600 hover:shadow-md transition-all duration-200"
    >
      <!-- Group Identity -->
      <div class="flex items-center gap-3">
        <div class="w-11 h-11 rounded-xl bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center flex-shrink-0 shadow-sm">
          <span class="text-indigo-700 dark:text-indigo-300 font-bold text-base">{{ group.name.charAt(0).toUpperCase() }}</span>
        </div>
        <div>
          <span class="font-semibold text-gray-800 dark:text-gray-100">{{ group.name }}</span>
          <p *ngIf="group.description" class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{{ group.description }}</p>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex items-center gap-2">
        <!-- View Members -->
        <button
          (click)="onViewMembers(group)"
          title="View members"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-indigo-100 dark:bg-gray-700 dark:hover:bg-indigo-900/40 text-gray-600 hover:text-indigo-700 dark:text-gray-300 dark:hover:text-indigo-300 font-medium rounded-lg transition-all duration-200 text-sm"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
          </svg>
          Members
        </button>

        <!-- Manage Users -->
        <button
          (click)="onAddUsers(group)"
          title="Manage users in this group"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-100 hover:bg-indigo-200 dark:bg-indigo-900/40 dark:hover:bg-indigo-900/70 text-indigo-700 dark:text-indigo-300 font-medium rounded-lg transition-all duration-200 text-sm"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
          </svg>
          Manage
        </button>

        <!-- Delete -->
        <button
          (click)="onDeleteGroup(group)"
          title="Delete group"
          class="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-all duration-200"
          aria-label="Delete group"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd" />
          </svg>
        </button>
      </div>
    </div>
  </div>

  <!-- Empty State -->
  <ng-template #noGroups>
    <div class="text-center py-16 bg-white dark:bg-gray-800 rounded-2xl border border-dashed border-gray-300 dark:border-gray-600">
      <div class="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-gray-700 rounded-2xl flex items-center justify-center">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      </div>
      <p class="text-gray-700 dark:text-gray-300 font-medium">No groups yet</p>
      <p class="text-sm text-gray-400 dark:text-gray-500 mt-1">Create your first group to organize your members.</p>
    </div>
  </ng-template>
</div>
